import { Icon } from "@iconify/react-with-api";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { configContext } from "../../components/config-provider";
import { SupportContact } from "../../components/support-contact";
import { APP_VERSION } from "../../config";
import { login as authLogin, LoginFailureReason } from "../../oauth";
import { IApplicationState } from "../../state/ducks";
import { authAsync } from "../../state/ducks/auth";
import { seedActivity } from "../../state/utils/activity";

/**
 * `LoginFailureReason` is what the SERVER can say. A blank field never reaches
 * it, so the page adds its own cause rather than widening the API's type.
 */
type HintCause = LoginFailureReason | "missingFields";

/**
 * Hint copy for the error box, chosen by cause.
 *
 * Deliberately error-specific: the general "contact technical support" line
 * lives below the Login button and is always on, so a reason whose message is
 * already actionable gets no hint rather than a restatement of it.
 */
const hintFor = (reason?: HintCause): string | null => {
	switch (reason) {
		case "locked":
			return "An administrator must unlock your account before you can sign in.";
		// No hint: these three messages already say what to do, and offering
		// "have you forgotten your password?" to someone who simply left a box
		// empty is a non-sequitur.
		case "passwordExpired":
		case "mfaRequired":
		case "missingFields":
			return null;
		default:
			return "If you have forgotten your password, technical support can reset it for you.";
	}
};

export const Login: React.FC = () => {
	const dispatch = useDispatch();
	const state = useSelector<IApplicationState, IApplicationState["auth"]>(
		(state) => state.auth
	);

	// Audience comes from config.json, so one build serves every environment.
	// Must match the clientId the portal is registered under on the auth
	// server, and the portal server's CLIENT_ID.
	const { authServer, clientId } = React.useContext(configContext);

	const history = useHistory();

	const [username, setUsername] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState("");
	// Named by cause, not by UI treatment. The previous `blocked` flag drifted
	// into claiming it covered password expiry, which is not a 402 at all.
	const [failureReason, setFailureReason] = React.useState<
		HintCause | undefined
	>();

	React.useEffect(() => {
		if (state.token.isAuthenticated) {
			history.replace("/dashboard");
		}
		// eslint-disable-next-line
	}, [state]);

	const handleLogin = async () => {
		if (!username || !password) {
			setError("Please enter username and password");
			setFailureReason("missingFields");
			return;
		}

		setLoading(true);
		setError("");
		setFailureReason(undefined);

		// Goes through the shared helper so a locked account gets the auth
		// server's own message ("this user account is locked. Please contact
		// an administrator to unlock it") instead of a blanket "invalid
		// username or password" — which would leave a locked user retrying,
		// and every retry extends the lock.
		const { token, error: failure, reason } = await authLogin(
			authServer,
			clientId,
			username,
			password
		);

		if (!token) {
			setError(failure ?? "Invalid username or password");
			setFailureReason(reason);
			setLoading(false);
			return;
		}

		// Start the inactivity clock from now. A stamp left in storage by a
		// previous session would otherwise sign the user straight back out.
		seedActivity();
		dispatch(authAsync(token));
	};

	const hint = hintFor(failureReason);

	return (
		<div className="login">
			<div className="form-wrapper">
				<div className="left">
					<img className="logo" src="/logo.svg" alt="EMMS Logo" />

					<div className="system-lines">
						<span>
							<span className="system-acronym-letter">E</span>lectronic
						</span>

						<span>
							<span className="system-acronym-letter">M</span>aintenance
						</span>

						<span>
							<span className="system-acronym-letter">M</span>anagement
						</span>

						<span>
							<span className="system-acronym-letter">S</span>ystem
						</span>
					</div>
				</div>
				<div className="right">
					{loading ? (
						<div className="spinner-wrapper">
							<div className="spinner"></div>
							<div className="status-message">Logging you in...</div>
						</div>
					) : (
						<>
							<div className="heading">Welcome Back</div>
							<div className="subheading">Login to your account</div>
							<div className="login-form">
								<div className="input-wrapper">
									<input
										className="login-input"
										type="text"
										placeholder="Username"
										value={username}
										onChange={(e) => setUsername(e.target.value)}
									/>
									<Icon icon="mdi:account" className="input-icon" width={16} height={16} />
								</div>
								<div className="input-wrapper">
									<input
										className="login-input"
										type="password"
										placeholder="Password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										onKeyDown={(e) => e.key === "Enter" && handleLogin()}
									/>
									<Icon icon="mdi:lock" className="input-icon" width={16} height={16} />
								</div>
								{error && (
									<div className="error-box">
										<div className="status-error">{error}</div>
										{hint && <div className="error-hint">{hint}</div>}
									</div>
								)}
								<button className="login-button" onClick={handleLogin}>
									Login
								</button>
								{/*
								  Always visible, and deliberately outside .error-box: someone who cannot
								  get in needs this number whether or not they have managed to trigger an
								  error yet, and the box's red tint made the contact itself read as a
								  fault. Neutral bounding box, larger type and an AA-contrast colour,
								  because at 12px #90a4ae it was ~2.6:1 and read as fine print.
								*/}
								<div className="support-box">
									<div className="support-text">
										If you need help signing in, please contact technical support.
									</div>
									<SupportContact />
								</div>
								{/* Quotable in a support call before signing in. */}
								<div className="app-version">v{APP_VERSION}</div>
							</div>
						</>
					)}
				</div>
			</div>
		</div >
	);
};
